import type { SubAbaDef, TabDef } from "./types";
import { ordenarPorNome } from "./ordem";

/**
 * AS QUATRO SUBABAS DOS EQUIPAMENTOS DE ENSAIO — descem dentro do item
 * "Equipamentos de Ensaio" do menu. O item de cima continua abrindo a lista de
 * sempre (o CrudView); as subabas acrescentam o caminho do equipamento na
 * bancada: cadastra, fica no estoque, sai (OS, cliente, calibração externa) e
 * volta — o mesmo do Controle de Seringas do Laboratório.
 *
 * O Laboratório tem a MESMA lista (a tela, components/EquipamentosEnsaioView.tsx,
 * é o mesmo arquivo nos dois): chaves, títulos e siglas iguais. Os ícones têm de
 * existir no ICONS daqui — um nome que falte derruba o menu inteiro.
 * Não são permissões novas: quem vê "Equipamentos de Ensaio" vê as quatro.
 */
export const SUBABAS_EQUIP_ENSAIO: SubAbaDef[] = [
  {
    key: "cadastro",
    titulo: "Cadastro",
    sigla: "CAD",
    descricao: "Cadastrar o equipamento: nome, marca, modelo, série, código de barras e certificado",
    icone: "gauge",
    tom: "green",
  },
  {
    key: "estoque",
    titulo: "Estoque",
    sigla: "EST",
    descricao: "Onde está cada equipamento, a calibração e os certificados",
    icone: "box",
    tom: "teal",
  },
  {
    key: "saida",
    titulo: "Saída de equipamento",
    sigla: "SAÍ",
    descricao: "Mandar equipamentos para uma OS, para o cliente ou para calibração externa",
    icone: "truck",
    tom: "emerald",
  },
  {
    key: "retorno",
    titulo: "Retorno de equipamento",
    sigla: "RET",
    descricao: "Bipar o que voltou de cada saída e devolver ao estoque",
    icone: "history",
    tom: "lime",
  },
];

/**
 * Abas do Sistema Operacional (menu lateral).
 *
 * Foco nas funções OPERACIONAIS: treinamentos, retrabalho, acidentes,
 * satisfação, calibração e liberação de equipamentos, além da equipe.
 *
 * Obras e Atividades NÃO têm aba de cadastro aqui — são geridas no
 * Planejamento (banco compartilhado) e aparecem apenas como INDICADORES
 * no Dashboard ("Obras por colaborador" e "Total de atividades executadas").
 */
export const TABS: TabDef[] = [
  {
    // CONTROLE DE TRAFOS — os trafos em reforma, ao vivo. Quem registra e
    // avança as etapas é a equipe, no app do colaborador ("Operacional" →
    // Trafos em Reforma), no mesmo banco. Ver server/trafos.ts.
    key: "controle_trafos",
    label: "Controle de Trafos",
    icon: "trafo",
    resource: "trafos",
    subtitle:
      "Trafos em reforma ao vivo: quantos há em cada etapa, quem mexeu por último e as movimentações da equipe conforme acontecem",
    custom: "controle_trafos",
    fields: [],
  },
  {
    key: "treinamentos",
    label: "Treinamentos",
    icon: "graduation",
    resource: "treinamentos",
    subtitle: "Treinamentos por colaborador — 'Concluir' grava a skill; o ícone de certificado anexa o PDF e o envia ao documento do colaborador no DP",
    compact: true,
    acoesLinha: ["concluir", "certificado"],
    fields: [
      { name: "colaborador_id", label: "Colaborador", type: "select", ref: "colaboradores", required: true },
      { name: "titulo", label: "Treinamento", type: "text", required: true },
      {
        name: "tipo",
        label: "Tipo",
        type: "select",
        options: ["NR", "Técnico", "SST", "Capacitação", "Reciclagem", "Integração", "Outro"],
      },
      { name: "instituicao", label: "Instituição", type: "text" },
      { name: "carga_horaria", label: "Carga (h)", type: "number" },
      { name: "data", label: "Realização", type: "date" },
      { name: "validade", label: "Reciclagem", type: "date" },
      { name: "situacao", label: "Situação", type: "status", computed: true },
      {
        name: "status",
        label: "Status",
        type: "status",
        options: ["Concluído", "Em andamento", "Agendado", "Cancelado"],
      },
    ],
  },
  {
    key: "retrabalho",
    label: "Retrabalho",
    icon: "repeat",
    resource: "retrabalho",
    subtitle: "Índices de retrabalho por colaborador (e pela média)",
    fields: [
      { name: "colaborador_id", label: "Responsável", type: "select", ref: "colaboradores", required: true },
      { name: "obra_id", label: "Obra", type: "select", ref: "obras" },
      { name: "descricao", label: "Descrição", type: "text" },
      { name: "quantidade", label: "Quantidade", type: "number" },
      {
        name: "motivo",
        label: "Motivo",
        type: "select",
        options: [
          "Falha de execução",
          "Especificação errada",
          "Material defeituoso",
          "Medição incorreta",
          "Erro de projeto",
          "Outro",
        ],
      },
      { name: "data", label: "Data", type: "date" },
      { name: "status", label: "Status", type: "status", options: ["Aberto", "Em correção", "Resolvido"] },
    ],
  },
  {
    key: "acidentes",
    label: "Acidentes / SST",
    icon: "hardhat",
    resource: "acidentes",
    subtitle: "Índices de acidentes por colaborador (e pela média) — segurança do trabalho",
    compact: true,
    fields: [
      { name: "colaborador_id", label: "Colaborador", type: "select", ref: "colaboradores", required: true },
      { name: "obra_id", label: "Obra", type: "select", ref: "obras" },
      { name: "descricao", label: "Descrição", type: "text" },
      {
        name: "tipo",
        label: "Tipo",
        type: "select",
        options: ["Típico", "Trajeto", "Doença ocupacional", "Quase acidente"],
      },
      {
        name: "gravidade",
        label: "Gravidade",
        type: "status",
        options: ["Sem lesão", "Leve", "Moderado", "Grave", "Óbito"],
      },
      { name: "dias_afastamento", label: "Afast. (dias)", type: "number" },
      { name: "data", label: "Data", type: "date" },
      {
        name: "status",
        label: "Status",
        type: "status",
        options: ["Registrado", "Em tratamento", "Encerrado"],
      },
    ],
  },
  {
    key: "satisfacao",
    label: "Satisfação",
    icon: "smile",
    resource: "satisfacao",
    subtitle: "Nível de satisfação ao cliente por obra",
    fields: [
      { name: "obra_id", label: "Obra", type: "select", ref: "obras" },
      { name: "colaborador_id", label: "Responsável", type: "select", ref: "colaboradores" },
      { name: "cliente", label: "Cliente", type: "text" },
      { name: "nota", label: "Nota (0–10)", type: "number" },
      {
        name: "nivel",
        label: "Nível",
        type: "status",
        options: ["Muito satisfeito", "Satisfeito", "Neutro", "Insatisfeito", "Muito insatisfeito"],
      },
      { name: "data", label: "Data", type: "date" },
      { name: "comentario", label: "Comentário", type: "textarea" },
    ],
  },
  {
    // CADASTRO da frota. As revisões ficam na aba "Manutenção de Veículos" —
    // um veículo tem muitas manutenções, e antes só cabia uma (ver
    // server/veiculos.ts).
    key: "veiculos",
    label: "Veículos",
    icon: "truck",
    resource: "veiculos",
    subtitle:
      "Cadastro da frota. A coluna 'Manutenção' avisa sozinha quando cada veículo precisa de revisão — ela vem da aba 'Manutenção de Veículos', onde as revisões são registradas.",
    // O veículo é identificado pela PLACA: é por ela que a lista fica em ordem.
    ordenarPor: "placa",
    // Documentos (CRLV, seguro...): anexados aqui, a equipe vê no Operacional.
    acoesLinha: ["documentos"],
    fields: [
      { name: "placa", label: "Placa", type: "text", required: true, placeholder: "ABC1D23" },
      { name: "modelo", label: "Modelo", type: "text", placeholder: "CHEV/ ONIX PLUS 1.0MT" },
      { name: "marca", label: "Marca", type: "text", placeholder: "Chevrolet" },
      { name: "ano", label: "Ano", type: "number", placeholder: "2023" },
      { name: "colaborador_id", label: "Responsável", type: "select", ref: "colaboradores" },
      { name: "km_atual", label: "KM atual", type: "number" },
      { name: "situacao_manutencao", label: "Manutenção", type: "status", computed: true },
      {
        name: "status",
        label: "Situação do veículo",
        type: "status",
        options: ["Ativo", "Em manutenção", "Inativo", "Vendido"],
      },
      { name: "observacao", label: "Observação", type: "textarea", hideInTable: true },
    ],
  },
  {
    // MANUTENÇÃO: uma linha por revisão/serviço, com histórico por veículo.
    // Usa a permissão de "Veículos" (ver TabDef.permissaoKey).
    key: "manutencao_veiculos",
    label: "Manutenção de Veículos",
    icon: "wrench",
    resource: "manutencoes",
    permissaoKey: "veiculos",
    subtitle:
      "Revisões e serviços de cada veículo — agende, registre o que foi feito e diga quando vence a próxima (por data ou por KM). O cadastro da frota fica na aba 'Veículos'.",
    fields: [
      { name: "veiculo_id", label: "Veículo", type: "select", ref: "veiculos", required: true },
      {
        name: "tipo_manutencao",
        label: "Tipo de manutenção",
        type: "select",
        options: [
          "Revisão",
          "Preventiva",
          "Corretiva",
          "Troca de óleo",
          "Pneus",
          "Freios",
          "Alinhamento e balanceamento",
          "Elétrica",
          "Funilaria e pintura",
          "Outro",
        ],
      },
      { name: "data_agendada", label: "Agendada para", type: "date" },
      { name: "data_realizada", label: "Realizada em", type: "date" },
      { name: "km_manutencao", label: "KM na manutenção", type: "number" },
      { name: "km_revisao", label: "KM da próxima revisão", type: "number" },
      { name: "proxima_revisao", label: "Próxima revisão", type: "date" },
      { name: "situacao", label: "Situação", type: "status", computed: true },
      {
        name: "status",
        label: "Status",
        type: "status",
        options: ["Agendada", "Em manutenção", "Concluída", "Cancelada"],
      },
      { name: "oficina", label: "Oficina / fornecedor", type: "text", hideInTable: true },
      { name: "custo", label: "Custo", type: "currency" },
      {
        name: "descricao",
        label: "Serviço executado",
        type: "textarea",
        hideInTable: true,
        placeholder: "O que foi feito (peças trocadas, o que foi verificado)",
      },
      { name: "observacao", label: "Observação", type: "textarea", hideInTable: true },
    ],
  },
  {
    key: "relatorio_inspecao",
    label: "Relatório de Inspeção",
    icon: "clipboard",
    resource: "inspecoes",
    subtitle:
      "Inspeções pré-uso dos veículos preenchidas pela equipe no app do colaborador — chegam aqui automaticamente, com as fotos de cada item e as não conformidades apontadas",
    custom: "relatorio_inspecao",
    fields: [],
  },
  {
    key: "equipamentos",
    label: "Equipamentos de Ensaio",
    icon: "gauge",
    resource: "equipamentos",
    subtitle:
      "Cadastro padrão dos equipamentos de ensaio do portal — o que está aqui aparece automaticamente no sistema de Relatórios. Anexe o certificado do laboratório (PDF) e o sistema lê a data de calibração",
    acoesLinha: ["calibracao"],
    ordenarPor: "nome",
    // "Novo" abre o cadastro completo das subabas (nome, marca, modelo, série,
    // código de barras e certificado); o lápis continua aqui, com todos os
    // campos, mas só manda o que mudou — a "Em atividade" também é escrita pela
    // bipagem das saídas (ver SUBABAS_EQUIP_ENSAIO).
    formNovo: "equipamento_ensaio",
    salvarSoAlterados: true,
    fields: [
      { name: "nome", label: "Equipamento", type: "text", required: true },
      { name: "numero_serie", label: "Nº de série", type: "text" },
      // Travado: o código só muda pela subaba Cadastro/Estoque, que confere se
      // ele já é de outro equipamento (o servidor nem aceita por aqui).
      { name: "codigo_barras", label: "Código de barras", type: "text", travado: true },
      // Só leitura: onde o equipamento está fisicamente (Em estoque, Em serviço,
      // Com cliente...). Muda pela bipagem das subabas Saída e Retorno e pela
      // baixa do Estoque — nunca por formulário. Fica perto do começo da tabela: no
      // fim ela só aparecia rolando para o lado.
      { name: "estoque_situacao", label: "Onde está", type: "status", soLeitura: true },
      // Modelo e Marca saem impressos no relatório técnico
      // ("NOME / MODELO / CERTIFICADO"): sem eles o documento sai com "—".
      // A coluna da marca se chama `fabricante` desde sempre (o Relatórios lê assim).
      { name: "modelo", label: "Modelo", type: "text" },
      { name: "fabricante", label: "Marca", type: "text" },
      {
        name: "tipo",
        label: "Tipo",
        type: "select",
        options: [
          "Isolação",
          "Resistência de contato",
          "Relação de espiras (TTR)",
          "Fator de potência",
          "Rigidez dielétrica",
          "Termografia",
          "Aterramento",
          "Corrente",
          "Multímetro",
          "Calibração",
          "Oscilografia",
          "Outro",
        ],
      },
      { name: "colaborador_id", label: "Responsável", type: "select", ref: "colaboradores" },
      { name: "data_calibracao", label: "Calibração", type: "date" },
      // Não é obrigatória: o certificado pode ficar para depois (equipamento
      // recém-chegado, ou antigo que nunca teve a data lançada).
      { name: "data_validade", label: "Validade", type: "date" },
      { name: "situacao", label: "Situação", type: "status", computed: true },
      {
        name: "autorizacao",
        label: "Em atividade",
        type: "status",
        // "Em uso" NÃO entra nesta lista: ele é automático (vem da OS aberta no
        // Relatórios, coluna "Em uso") e não se marca à mão.
        // "Bloqueado" e "Autorizado" estão aqui porque a coluna os recebe de
        // outros lugares (a baixa e o equipamento que não voltou de uma saída
        // gravam "Bloqueado"; o Planejamento grava "Autorizado"). Fora da lista,
        // o valor de verdade aparecia como "Selecione..." no lápis.
        options: ["Em atividade", "Disponível", "Em manutenção", "Em calibração", "Bloqueado", "Autorizado"],
      },
      // Só leitura: sai das OS ABERTAS do Relatórios, não do cadastro daqui.
      // Concluir, cancelar ou excluir a OS libera o equipamento sozinho. Com o
      // Relatórios fora do ar a coluna fica "—" e a aba continua normal.
      { name: "em_uso", label: "Em uso", type: "status", soLeitura: true },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  {
    key: "avaliar_folga",
    label: "Avaliar Folga",
    icon: "clipboard",
    resource: "agenda",
    subtitle: "Aprove ou reprove as folgas da equipe (marcadas na agenda). Ao reprovar, informe o motivo.",
    custom: "avaliar_folga",
    fields: [],
  },
  {
    key: "liberacoes",
    label: "Liberações",
    icon: "clipboard",
    resource: "liberacoes",
    subtitle:
      "Liberação de equipamentos para determinada atividade — informe a OS e o restante é preenchido sozinho; dá para pedir a separação dos insumos ao Almoxarifado",
    compact: true,
    acoesLinha: ["liberar"],
    // "Novo" e o lápis abrem o formulário próprio da liberação (LiberacaoModal).
    formCustom: "liberacao",
    fields: [
      { name: "equipamento_id", label: "Equipamento", type: "select", ref: "equipamentos", required: true },
      // Nº da OS: o formulário busca a ordem no sistema de Relatórios e preenche
      // cliente, tipo de serviço, período e responsável a partir dela.
      { name: "os_numero", label: "OS", type: "text" },
      { name: "atividade", label: "Atividade", type: "text" },
      { name: "obra_id", label: "Obra", type: "select", ref: "obras" },
      { name: "obra_cliente", label: "Cliente", type: "text", computed: true },
      { name: "obra_tipo_servico", label: "Tipo de serviço", type: "text", computed: true },
      { name: "obra_status", label: "Status da obra", type: "status", computed: true },
      { name: "colaborador_id", label: "Liberado para", type: "select", ref: "colaboradores" },
      { name: "data_liberacao", label: "Liberação", type: "date" },
      { name: "data_retorno", label: "Retorno", type: "date" },
      {
        name: "status",
        label: "Status",
        type: "status",
        options: ["Liberado", "Pendente", "Bloqueado", "Devolvido"],
      },
      { name: "observacao", label: "Observação", type: "textarea" },
    ],
  },
  {
    // APROVAR AGENDAMENTO — o Planejamento manda para cá o checkpoint da
    // atividade (datas + equipe) e é a aprovação do gestor que marca a agenda.
    // Ver server/aprovacoes-agenda.ts.
    key: "aprovar_agendamento",
    label: "Aprovar Agendamento",
    icon: "calendarCheck",
    resource: "aprovacoes-agenda",
    subtitle:
      "Pedidos do Planejamento aguardando o aval do gestor — aprovar marca os dias na agenda da equipe",
    custom: "aprovar_agendamento",
    fields: [],
  },
  {
    key: "painel_relatorios",
    label: "Relatórios",
    icon: "file",
    resource: "relatorios",
    subtitle:
      "Painel administrativo dos relatórios: o que está pendente, o que já foi enviado e busca por OS ou cliente (dados do sistema de Relatórios)",
    custom: "painel_relatorios",
    fields: [],
  },
  {
    key: "almoxarifado",
    label: "Almoxarifado",
    icon: "box",
    resource: "almoxarifado",
    subtitle: "Consulta de insumos e solicitação de separação ao Almoxarifado",
    custom: "almoxarifado",
    fields: [],
  },
  {
    key: "colaboradores",
    label: "Equipe",
    icon: "users",
    resource: "colaboradores",
    subtitle:
      "Equipe técnica espelhada do RH/DP — o cadastro é feito lá e desce para cá. A IA sugere treinamentos por colaborador (✦) e cada um tem sua pasta de certificados",
    acoesLinha: ["treinoIa", "pasta"],
    fonteRh: true,
    ordenarPor: "nome",
    fields: [
      { name: "nome", label: "Nome", type: "text", required: true, origemRh: true },
      { name: "cargo", label: "Cargo", type: "text", origemRh: true },
      {
        name: "setor",
        label: "Setor",
        type: "select",
        // Setores em ordem alfabética, como toda lista de nomes (src/ordem.ts).
        options: ordenarPorNome(
          ["Operações", "Manutenção", "Engenharia", "Ensaios", "Segurança", "Planejamento", "Logística"],
          (s) => s,
        ),
        origemRh: true,
      },
      { name: "email", label: "E-mail", type: "email", origemRh: true },
      { name: "telefone", label: "Telefone", type: "text", origemRh: true },
      { name: "status", label: "Status", type: "status", options: ["Ativo", "Inativo", "Afastado"], origemRh: true },
      // ÚNICO campo do colaborador que se altera aqui (por isso sem `origemRh`):
      // as competências são marcadas na equipe e sobem para o cadastro do RH/DP.
      { name: "skills", label: "Skills / Competências", type: "skills" },
    ],
  },
  {
    key: "usuarios",
    label: "Usuários",
    icon: "users",
    resource: "usuarios",
    subtitle: "Acessos ao sistema — crie o login do colaborador, vincule ao seu cadastro e marque as abas que ele pode acessar",
    ordenarPor: "nome",
    fields: [
      { name: "nome", label: "Nome", type: "text", required: true },
      { name: "usuario", label: "Usuário (login)", type: "text", required: true },
      { name: "email", label: "E-mail", type: "email" },
      { name: "cargo", label: "Cargo", type: "text", placeholder: "Contém 'admin' = acesso total" },
      {
        name: "colaborador_id",
        label: "Colaborador vinculado",
        type: "select",
        ref: "colaboradores",
        placeholder: "Vincula o login ao colaborador (avisos e recursos por pessoa)",
      },
      {
        name: "senha",
        label: "Senha",
        type: "password",
        hideInTable: true,
        placeholder: "Em branco mantém a senha atual",
      },
      { name: "ativo", label: "Situação", type: "select", options: ["Ativo", "Inativo"] },
      {
        name: "permissoes",
        label: "Permissões (abas liberadas)",
        type: "permissoes",
        hideInTable: true,
      },
    ],
  },
  {
    /* Aba de todos os sistemas: o pedido cai na fila do Desenvolvimento. */
    key: "solicitar_atualizacao",
    label: "Solicitar Atualização",
    icon: "wrench",
    resource: "solicitar_atualizacao",
    subtitle:
      "Marque uma reunião com o Desenvolvimento, peça um ajuste ou avise um erro. O pedido vai direto para quem cuida dos sistemas, com o seu nome e a data.",
    fields: [],
  },
];

/**
 * Itens de MENU que podem ser liberados/bloqueados por usuário (controle de
 * acesso). Dashboard e Agenda não estão em TABS (são fixos no App), por isso
 * entram aqui manualmente. "usuarios" fica de fora — é sempre só do admin.
 */
export const MENU_PERMISSOES: { key: string; label: string }[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "agenda", label: "Agenda" },
  // Aba com `permissaoKey` não vira checkbox próprio: ela anda junto com a aba
  // dona da permissão (Manutenção de Veículos ↔ Veículos).
  ...TABS.filter((t) => t.key !== "usuarios" && !t.permissaoKey).map((t) => ({
    key: t.key,
    label: t.label,
  })),
];

/** Todas as chaves de permissão (usado para "sem restrição" = tudo marcado). */
export const TODAS_PERMISSOES: string[] = MENU_PERMISSOES.map((m) => m.key);

/** Permissões padrão de um NOVO usuário colaborador (o pedido do operacional). */
export const PERMISSOES_COLABORADOR: string[] = ["agenda"];

/** Respostas possíveis de cada item do checklist de inspeção ("selecionar"). */
export const INSPECAO_RESPOSTAS: string[] = ["Conforme", "Não conforme", "N/A"];

/**
 * Checklist de inspeção pré-uso do veículo. Cada item é uma "pergunta de
 * selecionar" respondida com INSPECAO_RESPOSTAS. Edite livremente esta lista.
 */
export const CHECKLIST_VEICULO: string[] = [
  "Pneus (estado e calibragem)",
  "Estepe",
  "Nível de óleo do motor",
  "Nível de água / arrefecimento",
  "Freios",
  "Freio de estacionamento",
  "Faróis, lanternas e setas",
  "Luz de freio",
  "Buzina",
  "Retrovisores",
  "Limpador de para-brisa / água",
  "Cinto de segurança",
  "Para-brisa (trincas)",
  "Documentação (CRLV)",
  "Extintor de incêndio",
  "Triângulo / macaco / chave de roda",
  "Combustível (nível)",
  "Vazamentos (óleo / água)",
  "Limpeza geral",
];

/**
 * Catálogo de competências (skills) do colaborador — a lista de marcação do
 * cadastro. Fica em ./skills-catalogo para o SERVIDOR também usar (o espelho
 * do RH/DP precisa saber quais competências são da lista padrão).
 */
export { SKILLS_CATALOGO } from "./skills-catalogo";
